import { SiteRouteMap } from 'es/app/views/site/RouteMap';
import { SystemRouteMap } from 'es/app/views/system/RouteMap';
import { useI18n } from 'es/core/hooks/useI18n';
import LogManager from 'es/core/logging/LogManager';
import { useMemo } from 'react';

type RouteMapNode = string | { [key: string]: RouteMapNode } | ((...args: Array<never>) => RouteMapNode);

type RouteEntry = {
    path: string;
    key: string;
};

const logger = LogManager.getLogger('useTitledRoutes');

const getRouteEntries = (node: RouteMapNode, keyPath: Array<string>): Array<RouteEntry> => {
    if (typeof node === 'string') return [{ path: node, key: keyPath.join('.') }];
    if (typeof node === 'function') return getRouteEntries(node(), keyPath);
    return Object.entries(node).flatMap(([key, child]) => getRouteEntries(child, [...keyPath, key]));
};

// Match order follows route map definition order: the first matching route wins, so
// within each route map, routes with literal segments (e.g. "/new") must be defined
// before sibling routes with dynamic ones (e.g. "/:id") that would otherwise take
// preference. Some route map entries resolve to the same path (e.g. a "search"
// function returning its sibling "root" path), so only the first entry per path is kept.
const ALL_ROUTE_ENTRIES = [
    ...getRouteEntries(SiteRouteMap, ['site']),
    ...getRouteEntries(SystemRouteMap, ['system']),
].filter(({ path }, index, entries) => entries.findIndex(entry => entry.path === path) === index);

export function useTitledRoutes() {
    const { l10n, i18n } = useI18n('app/views', 'browserTitle');

    return useMemo(() => {
        const untitledEntries = ALL_ROUTE_ENTRIES.filter(
            ({ key }) => !i18n.exists(`browserTitle.${key}`, { ns: 'app/views' })
        );
        if (untitledEntries.length > 0) {
            const errorMessage = `Every route must have a browser title. Add entries to the translation files in app/views/_i18n for the following keys:\n${untitledEntries.map(({ key }) => `browserTitle.${key}`).join('\n')}`;
            if (import.meta.env.DEV) {
                throw new Error(errorMessage);
            }
            logger.error(errorMessage);
        }

        return ALL_ROUTE_ENTRIES.map(({ path, key }) => ({ path, title: l10n(key) }));
    }, [l10n, i18n]);
}
